import React from "react";
import PlayerShowNext from "components/PlayerShowNext";
import { AssetTypes } from "constant/AssetTypes";
import { useLocation, Location } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { adjustVolume } from "redux/AudioPlayerRedux";
import { addLastWatch, adjustVolumeVideo } from "redux/VideoPlayerRedux";
import VideoService from "services/video/VideoService";
import { ContentTypes } from "constant/ContentTypes";
import videojs from "video.js";

// add viewport height and width
const initialOptions: videojs.PlayerOptions = {
  controls: true,
  fluid: true,
  width: 1920,
  height: 1080,
  controlBar: {
    volumePanel: {
      inline: false,
    },
  },
};


const CreatorPlayer = ({ videoDetails, innerHeight, setInnerHeight }: any) => {
  const location = useLocation();
  const { currentTime } = location.state || {};
  // console.log("currentTime: ", currentTime);

  const dispatch = useDispatch<any>();
  const { slug } = useParams();
  const videoNode = useRef<HTMLVideoElement | null>(null);
  const player = useRef<videojs.Player>();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const iframeURL = process.env.REACT_APP_VIDEOPLAYER_IFRAME_URL;
  // Expected postMessage origin for the iframe above, derived from its own
  // URL instead of the separate REACT_APP_PLAYERHOST env var - that var has
  // to be kept in sync with REACT_APP_VIDEOPLAYER_IFRAME_URL by hand and
  // had drifted out of sync before (see VideoPlayer.tsx), silently
  // dropping every message the player posted back with no visible error.
  const BASE_URL = (() => {
    try {
      return iframeURL ? new URL(iframeURL).origin : undefined;
    } catch {
      return undefined;
    }
  })();
  const { currentVideo, isFetching } = useSelector(
    (state: any) => state?.video
  );
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  // const [innerHeight, setInnerHeight] = React.useState<number>(
  //   window.innerWidth / 2
  // );
  const [lastWatchTime, setLastWatchTime] = useState<number>(0);
  const [duration, setDuration] = useState<any>(0);
  const [showNext, setShowNext] = useState<boolean>(false);
  const [sendCount, setSendCount] = useState<boolean>(false);

  const [timeArr, setTimeArr] = useState<any[]>([]);
  const [triggerRemoveLastWatch, setTriggerRemoveLastWatch] =
    useState<boolean>(false);
  const volume = useSelector((state: any) => state?.video?.volume);

  useEffect(() => {
    // console.log("Current Video: " + currentVideo.url);

    // A creator video has no `season_name` (that field only exists on
    // movie/tv-series objects), so this is always a creator video here -
    // this player is only ever mounted for /stream/:slug/:stream (creator
    // video) routes, never for movies/tv series (those use VideoPlayer.tsx).
    let objectToSave: any = {
      id: currentVideo?.id,
      type: AssetTypes.CreatorVideo,
      stream: currentVideo?.stream_key,
      currentTime: lastWatchTime,
      slug: slug,
      title: currentVideo?.title,
      duration: duration,
      thumbnail: currentVideo?.thumbnail_url,
    };
    // saveDataToLocalStorage(objectToSave);
    if (lastWatchTime > 8 && duration > 5) {
      dispatch(addLastWatch(objectToSave));

      if (loggedInUser) {
        if ((lastWatchTime / duration) * 100 > 95 && !triggerRemoveLastWatch) {
          // remove the last watch if user reach 95% of the totla duration of the video
          const apiObj = {
            content_id: currentVideo?.id,
            content_type: ContentTypes.PUBLICATION_VIDEO,
          };
          VideoService.removeLastWatch(apiObj);
          setTriggerRemoveLastWatch(true);
        } else {
          //  else update the last watch every 20 seconds
          if (lastWatchTime % 20 === 0) {
            const apiObj = {
              content_id: currentVideo?.id,
              current_time: lastWatchTime,
              duration: duration,
            };

            VideoService.updateCreatorVideoLastWatch(apiObj);
          }
        }
      }
    }

    if (currentVideo?.season_name) {
      showNextSeries(lastWatchTime);
    }

    if (!sendCount) {
      updateTimeArray(lastWatchTime);
    }
    adjustHeight();
  }, [lastWatchTime]);

  const updateTimeArray = (time: number) => {
    if (time - timeArr[timeArr.length - 1] === 10) {
      setTimeArr([...timeArr, time]);
    } else {
      setTimeArr([time]);
    }
  };

  useEffect(() => {
    if (timeArr[timeArr.length - 1] - timeArr[0] === 10) {
      if (!sendCount) {
        // console.log('--------------- update count ---------------------------');
        updatePlayCount();
      }
      setSendCount(true);
    }
  }, [timeArr]);

  useEffect(() => {
    setTimeout(() => {
      const videoJsOptions = {
        sources: [
          {
            controls: true,
            responsive: true,
            fluid: true,
            src: currentVideo?.url,
            currentSrc: currentVideo?.url,
            buffered: [0.0, 0.04],
            // type: 'video/mp4',
          },
        ],
      };
      player.current?.dispose();
      if (videoNode.current) {
        player.current = videojs(videoNode.current, {
          ...initialOptions,
          ...videoJsOptions,
        }).ready(function () {
          videoNode.current?.play();
        });
      }
      return () => {
        if (player.current) {
          player.current.dispose();
        }
      };
    }, 3000);
  }, [currentVideo]);

  // console.log(currentVideo);
  const handleMessage = (event: any) => {
    if (event.origin === BASE_URL) {
      // if (event.origin === 'http://127.0.0.1:5500') {
      // console.log(event);
      const receivedData = event.data;
      if (typeof receivedData === "number") {
        // console.log(Math.floor(receivedData));

        setLastWatchTime(Math.floor(receivedData));
        // console.log("trigger nextt", Math.floor(receivedData) / duration);
      }
      if (event.data.type === "duration") {
        // console.log('duration', event.data.time);
        setDuration(event.data.time);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    const handleVolume = (event: any) => {
      if (
        event.source === iframeRef?.current?.contentWindow &&
        event.data.type === "volumeChange"
      ) {
        dispatch(adjustVolume(event.data.updatedVolume));
        dispatch(adjustVolumeVideo(event.data.updatedVolume));
      }
    };
    window.addEventListener("message", handleVolume);
    return () => {
      window.removeEventListener("message", handleVolume);
    };
  }, []);

  const updatePlayCount = () => {
    const obj = {
      content_id: currentVideo?.season_name
        ? currentVideo?.tv_series_id
        : currentVideo?.id,
      content_type: "creator_video",
    };

    VideoService.updatePlayCount(obj);
  };

  const showNextSeries = (receivedData: number) => {
    // console.log(
    //   'time to show next',
    //   receivedData,
    //   'action',
    //   duration - Math.floor(receivedData)
    // );
    if (duration > 300) {
      if (duration - Math.floor(receivedData) < 60) {
        // console.log('trigger show next', duration - Math.floor(receivedData));
        setShowNext(true);
      } else {
        setShowNext(false);
      }
    } else if (duration < 300 && duration > 100) {
      if (duration - Math.floor(receivedData) < 30) {
        // console.log('trigger show next', duration - Math.floor(receivedData));
        setShowNext(true);
      } else {
        setShowNext(false);
      }
    } else {
      setShowNext(false);
    }
  };

  // console.log('adTrigger : ' + adTrigger);
  const adjustHeight = () => {
    const aspectRatio = 16 / 9;

    if (window.innerWidth > 1500) {
      setInnerHeight((window.innerWidth * 0.7) / aspectRatio);
    } else if (window.innerWidth > 1300) {
      setInnerHeight((window.innerWidth * 0.7) / aspectRatio);
    } else if (window.innerWidth > 1100) {
      setInnerHeight((window.innerWidth * 0.62) / aspectRatio);
    } else if (window.innerWidth > 940) {
      setInnerHeight((window.innerWidth * 0.68) / aspectRatio);
    } else if (window.innerWidth > 768) {
      setInnerHeight((window.innerWidth * 0.65) / aspectRatio);
    } else if (window.innerWidth > 640) {
      setInnerHeight((window.innerWidth * 1) / aspectRatio);
    } else if (window.innerWidth > 480) {
      setInnerHeight((window.innerWidth * 1.2) / aspectRatio);
    } else {
      setInnerHeight((window.innerWidth * 1.0) / aspectRatio);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      adjustHeight();
    };

    adjustHeight();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="w-full mx-auto mt-2 max-sm:h-auto max-sm:w-full rounded-xl max-md:w-full">
      {/* <video
        ref={videoNode}
        poster={currentVideo?.thumbnail_url}
        id="my-player"
        className="video-js vjs-luxmty vjs-16-9"
        src="http://127.0.0.1:5500/iframe/VideoPlayer.html"
      /> */}

      {!isFetching && (
        <iframe
          title={`AYOZAT Video Player`}
          ref={iframeRef}
          width="100%"
          height={`${innerHeight}px`}
          // src={"http://127.0.0.1:5500/iframe/VideoPlayer.html"}
          src={iframeURL}
          onLoad={() => {
            const data = {
              source: "my-video",
              src: currentVideo?.url,
              userCurrentTime: currentTime
                ? currentTime
                : currentVideo?.userCurrentTime,
              poster: currentVideo?.thumbnail_url,
              vastEnabled: true,
              vastTagPreroll: currentVideo?.vast_tag_url,
              pre_ad: currentVideo?.pre_ad,
              vastDuration: currentVideo?.duration
                ? currentVideo?.duration
                : 10,
              volume: volume,
            };
            const iframeWindow = iframeRef.current?.contentWindow;
            iframeWindow?.postMessage(JSON.parse(JSON.stringify(data)), "*");
          }}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen"
          loading="lazy"
        ></iframe>
      )}

      {showNext ? (
        <PlayerShowNext
          videoDetails={videoDetails}
          show={showNext}
          setShowNext={setShowNext}
        />
      ) : (
        <></>
      )}
      {/* <PlayerShowNext
        videoDetails={videoDetails}
        show={true}
        setShowNext={setShowNext}
      /> */}
    </div>
  );
};

export default CreatorPlayer;
